// Client-side monotonic-read guard for the agent timeline (2026-07-19). The UI
// hydrate path REPLACES the displayed timeline with a fetched snapshot; if that
// snapshot is stale/lagging (an eventually-consistent PG-mirror page, or a reconnect
// race) it can be SHORTER than what the user already sees, and the wholesale replace
// then DELETES on-screen messages. This pure decision — evaluated against the highest
// committed `maxSeq` the stream has accepted so far (from fetch responses AND live
// committed frames) — rejects any snapshot that would move the timeline BACKWARD.
//
// `mode` is the single legitimate exception: a rewind intentionally SHRINKS the
// timeline (maxSeq drops to a new, smaller epoch), so it must be accepted and reset
// the high-water mark. `snapshotMaxSeq` undefined = an older daemon that doesn't send
// it → accept (legacy behavior; nothing to compare against).
//
// Pure + exported so the invariant is unit-tested rather than re-derived inside the
// Svelte store — the same discipline the relay-side resolveSessionReadSource seam uses.

export interface TimelineSnapshotInput {
  // The highest committed maxSeq the stream currently holds, or null if none yet.
  currentMaxSeq: number | null;
  // The committed high-water maxSeq of the incoming snapshot, or undefined if the
  // daemon didn't send one.
  snapshotMaxSeq: number | undefined;
  // Either mode bypasses the staleness check below.
  // "refresh" is a normal re-read that may legitimately be BEHIND what is on screen
  // (a cache paint, a settled session's poll); "rewind" is a deliberate truncation.
  // Both bypass the strictly-behind check — they differ in what the CALLER may keep,
  // not in what this guard decides. One boolean used to mean both, which is why a
  // reload and a rewind were indistinguishable here.
  mode?: "refresh" | "rewind";
  // How many rows the incoming snapshot carries. A ZERO-row page is never
  // authoritative — see the `mode` + empty rule in evaluateTimelineSnapshot.
  snapshotCount?: number;
  // How many entries the stream already shows. Used only to tell "the session is
  // genuinely new" (nothing on screen) from "the read source returned nothing".
  currentCount?: number;
  // A rewind to a life the mirror retired the old one for, holding none of its rows yet
  // (knowledge/125): empty on purpose, so the empty rule does not apply.
  retired?: boolean;
}

export interface TimelineSnapshotDecision {
  // Whether to apply (replace with) the snapshot. false → keep the fresher view.
  accept: boolean;
  // The high-water mark to store after applying. Only meaningful when accept=true.
  nextMaxSeq: number | null;
  // Why a snapshot was refused: an empty page over a populated view, or a stale one.
  refused?: "empty_page" | "stale";
}

export function evaluateTimelineSnapshot(input: TimelineSnapshotInput): TimelineSnapshotDecision {
  const { currentMaxSeq, snapshotMaxSeq, mode, snapshotCount, currentCount } = input;
  const authoritative = mode !== undefined;

  // An EMPTY page never replaces a non-empty timeline, whatever the mode.
  //
  // This is the hole the 2026-08-30 report fell through: SessionWorkView passes
  // `mode: refresh` while settled, so for a SETTLED session (status done/paused — the
  // state a shared work session sits in most of the time) that is permanently on and the
  // staleness check below is bypassed on every re-hydrate. A read that came back with
  // nothing then wiped the whole transcript, and the view fell to "No work in this
  // session yet." Reported verbatim: the history renders, seconds later it is empty,
  // a reload brings it back, and it empties again.
  //
  // Zero rows is the signature of a read-source MISS (the owning daemon offline, a PG
  // mirror page that has not landed), not of an empty session: a work session always
  // holds at least the prompt that opened it. The bypass exists for a rewind — a cold
  // resume restarting seq at 1 — and a rewind still has rows. So the exception is
  // narrow and cannot mask a legitimate shrink.
  //
  // `currentCount` 0 (or absent) ⇒ nothing on screen to protect: a genuinely new
  // session must still be free to hydrate empty and render its empty state.
  const retiredRewind = input.retired === true && mode === "rewind";
  if ((snapshotCount ?? -1) === 0 && (currentCount ?? 0) > 0 && !retiredRewind) {
    return { accept: false, nextMaxSeq: currentMaxSeq, refused: "empty_page" };
  }

  // Either mode is authoritative: accept and reset the mark to this page's max (or
  // null when the daemon sends none). A rewind is the case that MEANS a smaller epoch;
  // a refresh normally raises it, and only outranks the mark when a cache paint or a
  // settled poll left it ahead.
  if (authoritative) {
    return { accept: true, nextMaxSeq: typeof snapshotMaxSeq === "number" ? snapshotMaxSeq : null };
  }

  // No comparable seq (old daemon, or first-ever hydrate) → accept; seed the mark.
  // Old daemon sends no maxSeq → nothing to compare; accept, mark unchanged.
  if (typeof snapshotMaxSeq !== "number") {
    return { accept: true, nextMaxSeq: currentMaxSeq };
  }

  // First-ever hydrate → accept and seed the mark.
  if (currentMaxSeq == null) {
    return { accept: true, nextMaxSeq: snapshotMaxSeq };
  }

  // The guard: a snapshot strictly BEHIND what we already hold is stale → reject,
  // keeping the fresher timeline (never move backward / delete on-screen messages).
  if (snapshotMaxSeq < currentMaxSeq) {
    return { accept: false, nextMaxSeq: currentMaxSeq, refused: "stale" };
  }

  // Fresh-or-equal → accept; the mark can only rise.
  return { accept: true, nextMaxSeq: Math.max(currentMaxSeq, snapshotMaxSeq) };
}
